import React, { useState, useRef, useEffect } from 'react'

export default function AppEditableTimeField({
  hour = '10',
  minute = '00',
  ampm = 'AM',
  onHourChange,
  onMinuteChange,
  onAmPmChange,
  disabled = false,
  className = '',
}) {
  const [hourOpen, setHourOpen] = useState(false)
  const [minuteOpen, setMinuteOpen] = useState(false)
  const [ampmOpen, setAmPmOpen] = useState(false)

  const hourRef = useRef(null)
  const minuteRef = useRef(null)
  const ampmRef = useRef(null)

  const hourPresets = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
  const minutePresets = [
    '00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'
  ]

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (hourRef.current && !hourRef.current.contains(e.target)) setHourOpen(false)
      if (minuteRef.current && !minuteRef.current.contains(e.target)) setMinuteOpen(false)
      if (ampmRef.current && !ampmRef.current.contains(e.target)) setAmPmOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleHourInput = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 2)
    onHourChange?.(clean)
  }

  const handleHourBlur = () => {
    let num = parseInt(hour, 10)
    if (isNaN(num) || num < 1) num = 1
    if (num > 12) num = 12
    onHourChange?.(String(num).padStart(2, '0'))
  }

  const handleMinuteInput = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 2)
    onMinuteChange?.(clean)
  }

  const handleMinuteBlur = () => {
    let num = parseInt(minute, 10)
    if (isNaN(num) || num < 0) num = 0
    if (num > 59) num = 59
    onMinuteChange?.(String(num).padStart(2, '0'))
  }

  return (
    <div className={`w-full h-10 flex items-center justify-between rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 select-none ${className}`}>
      
      {/* ── Hour Input + Preset Dropdown ── */}
      <div ref={hourRef} className="relative flex items-center">
        <input
          type="text"
          value={hour}
          disabled={disabled}
          onChange={(e) => handleHourInput(e.target.value)}
          onBlur={handleHourBlur}
          placeholder="00"
          className="h-7 w-[2.6rem] rounded-[0.9rem] border border-outline-variant/30 bg-surface-container-lowest text-center text-xs font-bold text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
          title="Type custom hour (1-12) or click arrow for presets"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setHourOpen((prev) => !prev)}
          className="ml-0.5 flex h-7 w-4 items-center justify-center rounded text-on-surface-variant hover:text-on-surface transition cursor-pointer"
          title="Select Hour Preset"
        >
          <span className="material-symbols-outlined text-xs">arrow_drop_down</span>
        </button>

        {hourOpen && (
          <div className="absolute left-0 top-full mt-1 z-[250] max-h-44 w-14 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-1 shadow-lg">
            {hourPresets.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => {
                  onHourChange?.(h)
                  setHourOpen(false)
                }}
                className={`w-full rounded-md py-1 text-center text-xs font-bold transition cursor-pointer ${
                  hour === h ? 'bg-primary text-on-primary' : 'text-on-surface hover:bg-surface-container-high'
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        )}
      </div>

      <span className="font-bold text-on-surface-variant text-xs">:</span>

      {/* ── Minute Input + Preset Dropdown ── */}
      <div ref={minuteRef} className="relative flex items-center">
        <input
          type="text"
          value={minute}
          disabled={disabled}
          onChange={(e) => handleMinuteInput(e.target.value)}
          onBlur={handleMinuteBlur}
          placeholder="00"
          className="h-7 w-[2.6rem] rounded-[0.9rem] border border-outline-variant/30 bg-surface-container-lowest text-center text-xs font-bold text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
          title="Type custom minute (0-59) or click arrow for presets"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMinuteOpen((prev) => !prev)}
          className="ml-0.5 flex h-7 w-4 items-center justify-center rounded text-on-surface-variant hover:text-on-surface transition cursor-pointer"
          title="Select Minute Preset"
        >
          <span className="material-symbols-outlined text-xs">arrow_drop_down</span>
        </button>

        {minuteOpen && (
          <div className="absolute left-0 top-full mt-1 z-[250] max-h-44 w-14 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-1 shadow-lg">
            {minutePresets.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  onMinuteChange?.(m)
                  setMinuteOpen(false)
                }}
                className={`w-full rounded-md py-1 text-center text-xs font-bold transition cursor-pointer ${
                  minute === m ? 'bg-primary text-on-primary' : 'text-on-surface hover:bg-surface-container-high'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── AM / PM Toggle ── */}
      <div ref={ampmRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAmPmOpen((prev) => !prev)}
          className="flex h-7 items-center gap-0.5 rounded-md border border-outline-variant/30 bg-surface-container-lowest px-2 text-xs font-bold text-primary shadow-2xs transition hover:bg-surface-container-high cursor-pointer"
        >
          <span>{ampm || 'AM'}</span>
          <span className="material-symbols-outlined text-xs text-on-surface-variant">unfold_more</span>
        </button>

        {ampmOpen && (
          <div className="absolute right-0 top-full mt-1 z-[250] w-14 rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-1 shadow-lg">
            {['AM', 'PM'].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  onAmPmChange?.(item)
                  setAmPmOpen(false)
                }}
                className={`w-full rounded-md py-1 text-center text-xs font-bold transition cursor-pointer ${
                  ampm === item ? 'bg-primary text-on-primary' : 'text-on-surface hover:bg-surface-container-high'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}
